import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Card } from '../components/ui/Card.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { hrApi } from '../services/hrApi.js';

/**
 * Wie erfasst diese Firma ihre Arbeitszeit?
 *
 * Die Entscheidung gehört dem Betrieb, nicht dem Anbieter: ein Lokal mit
 * Schichtbetrieb will die Stempeluhr und sonst nichts - wer nachträglich
 * Stunden tippen kann, kann sie auch erfinden. Ein Büro mit Aussendienst
 * will das Gegenteil, dort sitzt niemand vor dem Bildschirm, wenn die Arbeit
 * beginnt. Beides ist richtig.
 *
 * Was hier NICHT eingestellt wird: der Korrekturweg des Personalwesens. Der
 * bleibt in jedem Fall offen, sonst wäre ein vergessener Stempel nicht mehr
 * geradezubiegen - und der Modus, der die Erfassung sauber halten soll,
 * würde ausgerechnet die falsche Zeile zementieren.
   */

function OptionRow({ value, label, description, selected, onSelect }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={`option-card${selected ? ' selected' : ''}`}
      onClick={() => onSelect(value)}
    >
      <span className="option-indicator" aria-hidden="true" />
      <span className="option-card-text">
        <span className="option-label">{label}</span>
        <span className="option-description">{description}</span>
      </span>
    </button>
  );
}

export function ZeiterfassungSettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [fehler, setFehler] = useState(null);
  const [gespeichert, setGespeichert] = useState(false);

  const stand = useQuery({
    queryKey: ['hr', 'settings'],
    queryFn: () => hrApi.einstellungen(),
  });

  const speichern = useMutation({
    mutationFn: (body) => hrApi.setzeEinstellungen(body),
    onSuccess: () => {
      setFehler(null);
      setGespeichert(true);
      queryClient.invalidateQueries({ queryKey: ['hr', 'settings'] });
      /* Die Uhr auf "Meine Zeit" richtet sich nach demselben Wert. Ohne
         diese Zeile stünde dort bis zum nächsten Laden noch der alte
         Modus - und der Knopf, den die Firma gerade abgeschaltet hat. */
      queryClient.invalidateQueries({ queryKey: ['self', 'stempel'] });
    },
    onError: (err) => {
      setGespeichert(false);
      setFehler(err?.message || t('common.loadError'));
    },
  });

  const modus = stand.data?.zeiterfassungModus;
  const maxStunden = stand.data?.stempelMaxStunden ?? 16;

  const optionen = [
    {
      value: 'BEIDES',
      label: t('settings.zeit.beides'),
      description: t('settings.zeit.beides.desc'),
    },
    {
      value: 'NUR_STEMPEL',
      label: t('settings.zeit.nurStempel'),
      description: t('settings.zeit.nurStempel.desc'),
    },
    {
      value: 'NUR_MANUELL',
      label: t('settings.zeit.nurManuell'),
      description: t('settings.zeit.nurManuell.desc'),
    },
  ];

  return (
    /* Kein zweiter Einleitungssatz: die Abschnittsüberschrift traegt ihn
       schon (SettingsPage gibt settings.zeit.intro als desc mit). Zweimal
       derselbe Absatz untereinander sieht nach einem Fehler aus - und war
       im Bild auch einer. */
    <Card title={t('settings.zeit.title')}>
      {stand.error && <div className="alert danger">{stand.error.message}</div>}

      {stand.data && (
        <>
          <div role="radiogroup" aria-label={t('settings.zeit.title')} className="option-group">
            {optionen.map((o) => (
              <OptionRow
                key={o.value}
                value={o.value}
                label={o.label}
                description={o.description}
                selected={modus === o.value}
                onSelect={(wert) => {
                  setGespeichert(false);
                  speichern.mutate({ zeiterfassungModus: wert });
                }}
              />
            ))}
          </div>

          {/* Ab wann ein Stempel als vergessen gilt. Er wird dann NICHT
              geschlossen - der Stopp verlangt eine bestätigte Endzeit. */}
          {modus !== 'NUR_MANUELL' && (
            <label className="field" style={{ maxWidth: '22rem', marginTop: 'var(--space-4)' }}>
              <span className="field-label">{t('settings.zeit.maxStunden')}</span>
              <input
                className="input"
                type="number"
                min="1"
                max="24"
                defaultValue={maxStunden}
                onBlur={(e) => {
                  const wert = Number(e.target.value);
                  if (wert >= 1 && wert <= 24 && wert !== maxStunden) {
                    setGespeichert(false);
                    speichern.mutate({ stempelMaxStunden: wert });
                  }
                }}
              />
              <span className="muted text-sm">{t('settings.zeit.maxStunden.hint')}</span>
            </label>
          )}

          {fehler && <div className="alert danger">{fehler}</div>}
          {gespeichert && !speichern.isPending && (
            <p className="muted text-sm">{t('settings.zeit.gespeichert')}</p>
          )}
        </>
      )}

      {stand.isLoading && <div className="skeleton-line" style={{ width: '14rem', height: '12px' }} />}
    </Card>
  );
}

export default ZeiterfassungSettingsCard;
